<template>
  <div class="w-full min-h-screen flex flex-col">
    <MainNav />
    <section class="w-full relative">
      <img
        :src="collection?.heroImage || '/image/83islands_storian.jpg'"
        :alt="collection?.name || 'Collection'"
        class="w-full h-[800px] object-cover object-[bottom_center] md:object-center"
      />

      <!-- Text Overlay -->
      <div class="mx-auto absolute mt-36 inset-0 z-20 w-full p-4 lg:p-36 flex flex-col items-center lg:items-start pointer-events-none">
        <h1 class="text-white font-maax text-4xl uppercase lg:text-6xl font-bold text-center lg:text-left max-w-3xl leading-tight drop-shadow-[0_5px_5px_rgba(0,0,0,0.3)] pointer-events-auto">
          {{ collection?.name }}
        </h1>
        <p class="text-white font-maax text-xl lg:text-2xl mt-4 text-center lg:text-left max-w-3xl drop-shadow-[0_5px_5px_rgba(0,0,0,0.3)] pointer-events-auto">
          {{ collection?.tagline }}
        </p>
      </div>

      <!-- Double Wave -->
      <div class="absolute -bottom-1 left-0 right-0 w-full overflow-hidden">
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none" class="w-full h-20 text-white">
          <path d="M985.66,92.83C906.67,72,823.78,31,743.84,14.19c-82.26-17.34-168.06-16.33-250.45.39-57.84,11.73-114,31.07-172,41.86A600.21,600.21,0,0,1,0,27.35V120H1200V95.8C1132.19,118.92,1055.71,111.31,985.66,92.83Z"
                fill="currentColor" class="opacity-25"/>
          <path d="M985.66,92.83C906.67,72,823.78,31,743.84,14.19c-82.26-17.34-168.06-16.33-250.45.39-57.84,11.73-114,31.07-172,41.86A600.21,600.21,0,0,1,0,27.35V120H1200V95.8C1132.19,118.92,1055.71,111.31,985.66,92.83Z"
                fill="currentColor"/>
        </svg>
      </div>
    </section>

    <main class="flex-grow w-full bg-white">
      <div class="container mx-auto p-4 lg:p-8">
        <!-- Back to Collections Link (Top) -->
        <div class="mt-8 mb-4">
          <router-link to="/collections" class="font-maax text-sm text-gray-500 hover:text-black">
            ← Back to All Collections
          </router-link>
        </div>

        <!-- Intro Paragraph -->
        <div v-if="collection?.introduction" class="mb-16">
          <p
            v-for="(paragraph, index) in collection.introduction"
            :key="index"
            class="font-maax text-black text-xl leading-relaxed mb-4"
          >
            {{ paragraph }}
          </p>
        </div>

        <!-- Products/Releases -->
        <div v-if="collection?.products && collection.products.length > 0" class="space-y-24 mb-16">
          <div
            v-for="(product, index) in collection.products"
            :key="index"
            :id="product.title.toLowerCase().replace(/\s+/g, '-')"
            class="flex flex-col lg:flex-row gap-4 lg:gap-0 items-center scroll-mt-24"
          >
            <!-- Bottle Image (Left on desktop, top on mobile) -->
            <div class="w-full lg:w-2/5 flex justify-center lg:justify-end">
              <div class="w-full max-w-lg h-[700px] flex items-center justify-center">
                <img
                  v-if="product.image"
                  :src="product.image"
                  :alt="product.title"
                  class="w-full h-full object-contain"
                />
                <div v-else class="text-gray-400 text-center p-8">
                  <svg class="w-24 h-24 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p class="font-maax text-sm uppercase">Bottle Image</p>
                </div>
              </div>
            </div>

            <!-- Content (Right on desktop, bottom on mobile) -->
            <div class="w-full lg:w-3/5 flex flex-col justify-center lg:pl-2">
              <div class="flex items-center justify-between mb-4">
                <div>
                  <h2 class="text-4xl lg:text-5xl font-maax font-bold uppercase text-black">
                    {{ product.title }}
                  </h2>
                  <p v-if="product.subtitle" class="font-maax text-xl uppercase text-black mt-2">
                    {{ product.subtitle }}
                  </p>
                </div>
                <span
                  v-if="product.status"
                  :class="[
                    'font-maax text-sm uppercase px-3 py-1 border border-black',
                    product.status === 'sold out' ? 'bg-black text-white' :
                    product.status === 'available' ? 'bg-green-100 text-black' :
                    product.status === 'limited quantities' ? 'bg-yellow-100 text-black' :
                    product.status === 'low in stock' ? 'bg-yellow-100 text-black' :
                    product.status === 'upcoming' ? 'bg-gray-100 text-black' : ''
                  ]"
                >
                  {{ product.status }}
                </span>
              </div>

              <!-- Unique Blend -->
              <div v-if="product.uniqueBlend" class="mb-6">
                <p class="font-maax text-black text-lg leading-relaxed italic">
                  {{ product.uniqueBlend }}
                </p>
              </div>

              <!-- Technical Details -->
              <div v-if="productDetails(product).length > 0 || product.availability" class="mb-6 border border-black">
                <div v-if="productDetails(product).length > 0" :class="detailGridClass(product)">
                  <div
                    v-for="detail in productDetails(product)"
                    :key="detail.label"
                    class="border-b border-r border-black p-3 text-center"
                  >
                    <p class="font-maax text-sm text-black">
                      <span class="font-bold">{{ detail.label }}</span><br>
                      {{ detail.value }}
                    </p>
                  </div>
                </div>
                <div v-if="product.availability" class="p-3 text-center">
                  <p class="font-maax text-sm text-black font-bold">
                    {{ product.availability }}
                  </p>
                </div>
              </div>

              <!-- Warning -->
              <div v-if="product.warning" class="mb-6 p-4 bg-yellow-50 border border-yellow-400 rounded">
                <p class="font-maax text-sm text-black">
                  <span class="font-bold">Warning:</span> {{ product.warning }}
                </p>
              </div>

              <!-- Story/Description -->
              <div v-if="product.story && product.story.length > 0" class="prose prose-lg max-w-none">
                <h3 class="font-maax text-2xl font-bold uppercase text-black mb-4">
                  {{ collection?.storyLabel || 'Storian' }}
                </h3>
                <p
                  v-for="(paragraph, pIndex) in product.story"
                  :key="pIndex"
                  class="font-maax text-black text-xl leading-relaxed mb-4"
                >
                  {{ paragraph }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Coming Soon Message -->
        <div v-else class="text-center py-16">
          <p class="font-maax text-2xl text-black mb-4">Coming Soon</p>
          <p class="font-maax text-lg text-gray-600">
            This collection is currently being prepared. Check back soon for updates.
          </p>
        </div>

        <!-- Back to Collections Link (Bottom) -->
        <div class="mt-8 mb-8 text-center">
          <router-link to="/collections" class="font-maax text-sm text-gray-500 hover:text-black">
            ← Back to All Collections
          </router-link>
        </div>
      </div>
    </main>

    <Footer class="relative" />
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useHead } from '@vueuse/head'
import MainNav from '../components/MainNav.vue'
import Footer from '../components/Footer.vue'

const route = useRoute()

const productDetails = (product) => {
  const details = [
    ['Casks', product.casks],
    ['Filled', product.filled],
    ['Age', product.age],
    ['Batch', product.batch],
    ['Bottles', product.bottles],
    ['Bottled', product.bottled],
    ['Volume', product.volume],
    ['ABV', product.abv ? `${product.abv}%` : null]
  ]

  return details
    .filter(([, value]) => value)
    .map(([label, value]) => ({ label, value }))
}

const detailGridClass = (product) => {
  const count = productDetails(product).length
  if (count <= 2) return 'grid grid-cols-2'
  if (count === 3) return 'grid grid-cols-2 lg:grid-cols-3'
  return 'grid grid-cols-2 lg:grid-cols-4'
}

// Collections data
const collectionsData = {
  'storian': {
    name: 'The Storian Collection',
    tagline: 'Rare spirits that tell their own story — shaped by chance, time, and the quiet hand of the islands.',
    heroImage: '/image/83islands_storian.jpg',
    storyLabel: 'Storian',
    introduction: [
      'The Storian Collection is about rums that are exceptional not only for their quality, but for the unique stories they carry. These are spirits shaped by circumstance, rarity, or experiment—bottles that capture a moment in time and invite it to be shared.',
    ],
    products: [
      {
        title: 'Lost Times',
        uniqueBlend: 'A UNIQUE BLEND OF 7 CASKS LOST TO THE EARTHQUAKE, SALVAGED AND REBORN. A RARE UNION OF EX-BRANDY, EX-RYE, EX-FRENCH BARRIQUE, NEW OAK, AND EX-RUM CASKS, EACH CONTRIBUTING ITS OWN LAYERS OF CHARACTER, COMPLEXITY, AND RESILIENCE.',
        story: [
          'Time is a quiet companion, moving unseen, shaping our days without demand. We seldom notice it—until it is gone. On December 17th, time fractured—lives were lost, moments stolen, and the ageing barrels in our distillery fell, spilling their quiet work into the earth. What remained was not just rum, but memory—each drop a reflection of what was, of what could have been.',
          'As rum makers, we place our spirit in barrels with faith in time itself. We wait, restless and eager, believing that with enough patience, the rum will become something greater. Yet too often, we forget that time is not just something to be spent—it is something to be lived. The earthquake was a brutal reminder that nothing—no craft, no ambition—is worth more than the time we have with the people we love.',
          'This release, Lost Times, is a tribute. To the lives we lost, to the seconds that will never return, and to the strength of the Ni-Vanuatu people who stand, rebuild, and carry time forward. It is the sum of many parts—the remnants of barrels that aged in patience, shaken but not lost, gathered into something whole once more.',
          'Raise a glass to the past, to the present, and to the future that we honour with every sip.'
        ],
        casks: '7 PARTIAL CASKS',
        filled: 'FEB 20 - APR 21',
        age: '3.65 to 4.8 YEARS',
        batch: 'ARB10',
        bottles: '645',
        bottled: '5 FEB 2025',
        abv: '40',
        year: '2025',
        volume: '750ml',
        availability: 'Sold out. Made in Vanuatu.',
        status: 'sold out',
        image: '/image/ARB10_quarter.png'
      },
      {
        title: 'The Forgotten Casks',
        subtitle: 'Release 1 — Driftwood',
        uniqueBlend: 'A BLEND OF TWO UNRECORDED BARRELS FROM OUR EARLIEST STOCKS. THE LOGS WERE LOST TO CYCLONES, EARTHQUAKES, OR TIME. THOUGHT TO BE MOLASSES RUM, AGED OVER FOUR YEARS IN VIRGIN OAK CHAR #2 & #3. WHAT REMAINS IS RICH, RESILIENT, AND RARE.',
        story: [
          'Not every story is meant to be known. Some barrels lose their names, yet keep their truth. We no longer hold the records, only the memory of oak and ocean, of fire turned gentle through the patience of years.',
          'What was once loss is now offering. Each drop reminds us that time cannot truly vanish; it only changes shape into flavour, into silence, into gratitude.',
          'This rum is what remains after certainty fades. It is proof that craft endures beyond control, that even the forgotten can mature into grace.',
          'Raise a glass to what we still have, to the hands that made it, to the time that shaped it, to the spirit that refuses to be lost.',
          'TASTING NOTE: A full-bodied sweet rum with pronounced notes of cherry, coconut, and fruitcake on the nose, leading into a warm, buttery palate layered with vanilla, toffee, and woody spice.'
        ],
        casks: 'Unique Blend of 2 Virgin Oak',
        filled: 'Unknown',
        age: 'Best estimate +4 years',
        batch: 'ARB13',
        bottles: '332',
        bottled: '27 Nov 2025',
        abv: '50',
        year: '2025',
        volume: '750ml',
        availability: 'Only 332 bottles. Made in Vanuatu.',
        status: 'available',
        image: '/image/ARB13_quarter.png'
      }
    ]
  },
  'haos': {
    name: 'Haos Collection',
    tagline: 'Vibrant bar exclusives made for mixing, sharing, and pouring Vanuatu by the glass.',
    heroImage: '/image/83islands_storian.jpg',
    storyLabel: 'Story',
    introduction: [
      'Haos is where many guests meet 83 Islands first: spirits built for the bar, made to mix cleanly, pour reliably, and carry a local story into every glass.',
      'The range brings together vodka, gin, cane rum, and blended rum expressions designed for easy service without giving up the craft, freshness, and Vanuatu origin that define the distillery.'
    ],
    products: [
      {
        title: 'Vodka',
        subtitle: 'Full Wheat Vodka',
        uniqueBlend: 'A clean, exceptionally smooth wheat vodka made in Vanuatu.',
        story: [
          'It opens with a gentle sweetness and moves across the palate with remarkable softness: no burn, no harsh edges, only quiet clarity.',
          'The finish lingers with a smooth mouthfeel that stays long after the sip is gone.'
        ],
        volume: '750ml',
        availability: 'Made in Vanuatu',
        status: 'available'
      },
      {
        title: 'Cucumber Dry Gin',
        subtitle: 'London Dry Style',
        uniqueBlend: 'A versatile, balanced dry gin built to mix flawlessly.',
        story: [
          'Juniper-forward with clean citrus lift and no single botanical fighting for attention.',
          'Made for classic G&Ts, Negronis, and house cocktails that need crisp structure and cool clarity.'
        ],
        volume: '750ml',
        abv: '41',
        availability: 'Made in Vanuatu',
        status: 'available',
        image: '/image/Cucumber Dry Gin_half.jpg'
      },
      {
        title: 'Yumi Rum',
        subtitle: 'Signature Sugarcane Rum',
        uniqueBlend: 'Locally grown, hand-cut sugarcane distilled in our hybrid copper pot still.',
        story: [
          'Agricole in style: grassy, bright, unmistakably fresh, and rooted in the field it came from.',
          'It is the foundation of any cocktail worth making, but clean and vivid enough to sip neat.'
        ],
        volume: '750ml',
        availability: 'Made in Vanuatu',
        status: 'available',
        image: '/image/SC-YRSV-750-YRB2_quarter.png'
      },
      {
        title: 'Tufala Cane & Cask',
        subtitle: 'Cane and Cask Rum',
        uniqueBlend: 'Two rums in one: aged sugarcane rum blended with unaged molasses rum.',
        story: [
          'A layered house rum with more complexity than expected at everyday pour strength.',
          'Bold enough to stand alone and built to lift cocktails with cane brightness and cask depth.'
        ],
        volume: '750ml',
        availability: 'Made in Vanuatu',
        status: 'available'
      }
    ]
  },
  'first-press': {
    name: 'First Press Collection',
    tagline: 'Pure origin cane juice rum, captured at its brightest and most immediate.',
    heroImage: '/image/83islands_storian.jpg',
    storyLabel: 'Story',
    introduction: [
      'This is where it all begins. First Press is our agricole-style rum in its purest form: single-farm sugarcane, hand-cut at sunrise, crushed within hours, and bottled to show Vanuatu terroir at its most immediate.',
      'These spirits are grassy, bright, and unmistakably fresh. They are made for guests who want to taste the land itself and for bartenders who need a rum that lifts any cocktail it touches.'
    ],
    products: [
      {
        title: 'Yumi Rum Special Vintage',
        subtitle: 'Single-Farm Cane Rum',
        uniqueBlend: 'Every batch tells a different story, traced back to the farmer or land that grew the cane.',
        story: [
          'Single-farm sugarcane is hand-cut and traced to the field it came from, giving each release its own origin and harvest character.',
          'Agricole in style, it is grassy, vibrant, and alive. No two batches are the same because no two harvests are the same.'
        ],
        volume: '750ml',
        availability: 'Made in Vanuatu',
        status: 'available',
        image: '/image/SC-YRSV-750-YRB2_quarter.png'
      },
      {
        title: 'Yumi Rum Grand Cru',
        subtitle: 'Seasonal Cane Selection',
        uniqueBlend: 'The finest cane of the season, selected for an extremely limited pure-origin release.',
        story: [
          'Grand Cru is drawn from the most exceptional sugarcane harvested each year.',
          'It is Yumi Rum at its rarest: pure, complex, unrepeatable, and made for those who want to taste what the land gave us at its very best.'
        ],
        volume: '750ml',
        availability: 'Extremely limited release',
        status: 'limited quantities'
      }
    ]
  },
  'nabanga': {
    name: 'Nabanga Collection',
    tagline: 'The first sip: younger aged rums that are bold, approachable, and ready for the next round.',
    heroImage: '/image/83islands_storian.jpg',
    storyLabel: 'Story',
    introduction: [
      'Nabanga is the banyan tree: roots that spread wide, welcoming all who arrive. This collection does the same through younger rums that are vibrant, energetic, and easy to love.',
      'These are the bottles within arm\'s reach: ready for cocktails, rocks pours, and shared moments while still carrying the story of where they came from.'
    ],
    products: [
      {
        title: 'Aged Yumi Rum',
        subtitle: 'Two Years Old',
        uniqueBlend: 'Yumi Rum aged for two years in used bourbon barrels.',
        story: [
          'The agricole brightness of Yumi is softened by time, bringing vanilla, caramel, and a warmth that lingers.',
          'It stays fresh while adding oak complexity, making it the entry point into aged 83 Islands rum.'
        ],
        age: '2 Years',
        volume: '750ml',
        abv: '40',
        availability: 'Made in Vanuatu',
        status: 'available',
        image: '/image/ARB4-Normal_quarter.png'
      },
      {
        title: 'High Ester Rum',
        subtitle: 'Funky Unaged Rum',
        uniqueBlend: 'Jamaican soul, Vanuatu-born, made for bold aromatic intensity.',
        story: [
          'Made using a muck pit analogue for full-flavoured funk straight off the still.',
          'This is the rum that makes you rethink what unaged can taste like.'
        ],
        volume: '750ml',
        abv: '40',
        availability: 'Made in Vanuatu',
        status: 'available'
      },
      {
        title: 'High Ester Rum OP',
        subtitle: 'High Proof',
        uniqueBlend: 'The same funk with more fire, bottled for cocktails that demand attention.',
        story: [
          'High Ester Rum OP keeps the bold aromatic profile and raises the proof for a fuller, more unapologetic experience.',
          'Built for drinks that need intensity, structure, and unmistakable rum character.'
        ],
        volume: '750ml',
        abv: '48',
        availability: 'Made in Vanuatu',
        status: 'available'
      }
    ]
  },
  'spiced-rum': {
    name: 'Spiced Rum Collection',
    tagline: 'Bold, memorable spiced rum made for fun.',
    heroImage: '/image/83islands_storian.jpg',
    storyLabel: 'Story',
    introduction: [
      'Our Spiced Rum Collection is built for the crowd that wants something bold, memorable, and a little different.',
      'Faea Kava brings local chilli and kava into a spiced rum that sparks curiosity and gives guests a flavour they will not find anywhere else.'
    ],
    products: [
      {
        title: 'Faea Kava',
        subtitle: 'Spiced Rum',
        uniqueBlend: 'Local chilli meets kava in a spiced rum that is unmistakably Vanuatu.',
        story: [
          'It opens with sweet, building heat and settles into the gentle tingle that only kava brings.',
          'Balanced spice, warmth, curiosity, and the urge to pour another.'
        ],
        batch: 'FK02',
        volume: '750ml',
        abv: '36.5',
        availability: 'Made in Vanuatu',
        warning: 'Please consume this product in moderation. While the kava content is not enough to produce a strong effect on its own, combining alcohol and kava may increase intoxication and impairment. It may also elevate the risk of liver harm, particularly for heavy drinkers or those with pre-existing liver conditions.',
        status: 'available',
        image: '/image/FK02_quarter.png'
      }
    ]
  },
  'kalakala': {
    name: 'Kalakala Collection',
    tagline: 'Colourful island flavour without borders.',
    heroImage: '/image/83islands_storian.jpg',
    storyLabel: 'Story',
    introduction: [
      'Kalakala means "colourful" in Bislama, and this collection lives up to its name. From citrus liqueurs made with local fruit to rich coffee and cacao expressions, this is the range that brings vibrancy and versatility to the bar.',
      'Every bottle is infused with real local ingredients where the recipe calls for them. The result is bright, generous, and unmistakably tied to Vanuatu.'
    ],
    products: [
      {
        title: 'Port Vila Dry Gin',
        subtitle: 'London Dry Style',
        uniqueBlend: 'A classic dry gin: balanced, precise, and versatile.',
        story: [
          'Juniper-forward with clean citrus lift and botanicals that know their place.',
          'Versatile enough for cocktails and distinctive enough to stand alone.'
        ],
        volume: '750ml',
        availability: 'Made in Vanuatu',
        status: 'available'
      },
      {
        title: 'Cucumber Dry Gin',
        subtitle: 'Cucumber and Local Citrus',
        uniqueBlend: 'Fresh cucumber and local citrus with the clean balance of a London dry.',
        story: [
          'Vacuum-distilled cucumber and local citrus make this our crispest gin expression.',
          'It is fresh and cooling without botanical overload: just clean, balanced clarity.'
        ],
        volume: '750ml',
        abv: '41',
        availability: 'Made in Vanuatu',
        status: 'available',
        image: '/image/Cucumber Dry Gin_half.jpg'
      },
      {
        title: 'Orangecello',
        subtitle: 'Arancio Verde Liqueur',
        uniqueBlend: 'Green orange liqueur in the style of limoncello.',
        story: [
          'Bright, zesty, and sun-soaked, made with local green oranges and a clean sweetness.',
          'Serve cold or mix freely when a drink needs citrus brightness and a golden-hour finish.'
        ],
        volume: '750ml',
        abv: '30',
        availability: 'Made in Vanuatu',
        status: 'available',
        image: '/image/Arancio-Verde-Liqueur_half.jpg'
      },
      {
        title: 'Pamplemello',
        subtitle: 'Grapefruit Liqueur',
        uniqueBlend: 'Vanuatu grapefruit captured in a bittersweet citrus liqueur.',
        story: [
          'Pamplemello brings soft honey undertones, tropical depth, and a lingering citrus finish.',
          'It is built for spritzes, tropical cocktails, and guests who want brightness with more structure.'
        ],
        volume: '750ml',
        availability: 'Made in Vanuatu',
        status: 'available'
      },
      {
        title: 'Pomelello',
        subtitle: 'Pomelo Liqueur',
        uniqueBlend: 'Local pomelo transformed into a delicate island liqueur.',
        story: [
          'Floral, bright, and gently sweet without overwhelming the palate.',
          'The gentlest of the citrus family and one of the most distinctly Vanuatu expressions in the range.'
        ],
        volume: '750ml',
        availability: 'Made in Vanuatu',
        status: 'available'
      },
      {
        title: 'Coffee Liqueur',
        subtitle: 'Tanna Coffee',
        uniqueBlend: 'Specially roasted Tanna coffee beans infused into rum.',
        story: [
          'Rich, deep, and grounded, made without artificial shortcuts.',
          'Built for espresso martinis, dessert pairings, and slow evenings alike.'
        ],
        volume: '750ml',
        abv: '36',
        availability: 'Made in Vanuatu',
        status: 'available',
        image: '/image/SL-CL30-750_half.jpg'
      },
      {
        title: 'Cacao Nibs Liqueur',
        subtitle: 'Malekula Cacao',
        uniqueBlend: 'Cacao nibs from Gaston Chocolat on Malekula Island.',
        story: [
          'Dark, earthy, and rare, this liqueur captures a different side of Vanuatu cacao.',
          'It is made for dessert cocktails, slow sipping, and guests who want chocolate with real depth.'
        ],
        volume: '750ml',
        availability: 'Made in Vanuatu',
        status: 'available',
        image: '/image/Cacao Nibs_half.jpg'
      },
      {
        title: 'Pineapple Rum',
        subtitle: 'Real Pineapple',
        uniqueBlend: 'Ripe pineapple infused into rum with no artificial flavour.',
        story: [
          'The profile leans toward roasted, caramelised, sun-warmed pineapple rather than candy.',
          'A tropical rum that actually tastes like the tropics.'
        ],
        volume: '750ml',
        availability: 'Made in Vanuatu',
        status: 'available'
      }
    ]
  },
  'lapita': {
    name: 'Lapita Collection',
    tagline: 'Specialty cask blends that balance originality with dependable craft.',
    heroImage: '/image/83islands_storian.jpg',
    storyLabel: 'Story',
    introduction: [
      'The Lapita people carried culture and innovation across the Pacific. This collection carries our story forward in the same spirit through specialty blends shaped by distinctive casks.',
      'Lapita sits at the heart of the portfolio: above the youthful energy of Nabanga, more accessible than our single casks, and built for regulars who come back for a reliable expression with real cask character.'
    ],
    products: [
      {
        title: 'Ex-Brandy Blend',
        subtitle: 'Specialty Cask Blend',
        uniqueBlend: 'Ex-brandy cask aged rum with a sweeter, rounder oak profile.',
        story: [
          'Rich with dried fruit and the softer side of oak.',
          'Made for rum sours and drinks where depth needs balance.'
        ],
        volume: '750ml',
        availability: 'Made in Vanuatu',
        status: 'available'
      },
      {
        title: 'Ex-Rye Blend',
        subtitle: 'Specialty Cask Blend',
        uniqueBlend: 'Ex-rye cask aged rum with a drier, spicier edge.',
        story: [
          'A bolder side of oak with a whisky-like structure.',
          'Try it in a rum Negroni when the drink needs spice, grip, and depth.'
        ],
        volume: '750ml',
        availability: 'Made in Vanuatu',
        status: 'available'
      }
    ]
  },
  'single-cask': {
    name: 'Single Cask Collection',
    tagline: 'The rare casks that rise above the rest.',
    heroImage: '/image/83islands_storian.jpg',
    storyLabel: 'Story',
    introduction: [
      'The Single Cask Collection is about the rare casks that rise above the rest. Each is chosen simply because it stands out—exceptional in flavour, balance, or character. No blending, no compromise—just the pure expression of one cask at its peak.',
      'Selection is driven by quality alone. These are the barrels that tell their own story, offering individuality that cannot be replicated.',
      'The Single Cask Collection embodies the pinnacle of our craft—spirits for connoisseurs who seek not just rum, but rarity itself.'
    ],
    products: [
      {
        title: 'Ex-Chardonnay',
        uniqueBlend: 'A second-fill French oak cask shaped first by Chardonnay and then by Vanuatu rum.',
        story: [
          'This cask once held the very first aged release in 83 Islands history, then was refilled and reborn.',
          'Lighter in grip than first-fill oak, with delicate fruit, soft vanilla, and a patient finish.'
        ],
        age: '3.9 Years',
        volume: '750ml',
        availability: 'Limited quantities. Made in Vanuatu.',
        status: 'limited quantities',
        image: '/image/ARB5_quarter.png'
      },
      {
        title: 'Ex-Red Wine',
        uniqueBlend: 'A five-year first-fill French barrique single cask shaped by red wine before rum.',
        story: [
          'Bold structure, dark fruit, and a buttery finish come through after ageing in a cask marked by wine and island climate.',
          'Aged through eight cyclones and 119 earthquakes, this rum carries the imprint of Vanuatu\'s restless environment.'
        ],
        age: '5 Years',
        volume: '750ml',
        availability: 'Limited quantities. Made in Vanuatu.',
        status: 'limited quantities',
        image: '/image/ARB8_half.jpg'
      },
      {
        title: 'Ex-Rye',
        uniqueBlend: 'Three years in first-fill American oak from Peerless Distilling in Kentucky.',
        story: [
          'The rye cask leaves its mark through candied orange, cherry, and fruit cake spice.',
          'Sweet, complex, and unmistakably shaped by one of America\'s finest craft distilleries.'
        ],
        casks: 'PRK517',
        filled: '17 Nov 2022',
        age: '3 Years',
        batch: 'ARB14',
        bottles: '184',
        bottled: '9 Dec 2025',
        volume: '750ml',
        abv: '47',
        availability: 'Limited quantities. Made in Vanuatu.',
        status: 'limited quantities',
        image: '/image/ARB14_quarter.png'
      },
      {
        title: 'Ex-Bourbon',
        uniqueBlend: 'Four years in first-fill American oak, balancing vanilla, caramel, and aged cane character.',
        story: [
          'Light dried fruit, a peppery edge, and gentle sweetness let the sugarcane speak clearly.',
          'Sweet, balanced, deeply familiar, and still unmistakably Vanuatu.'
        ],
        age: '4 Years',
        volume: '750ml',
        availability: 'Limited quantities. Made in Vanuatu.',
        status: 'limited quantities'
      },
      {
        title: 'Ex-Rum',
        uniqueBlend: 'Five years in an ex-rum cask: rum aged in the memory of rum.',
        story: [
          'Dried fruit and pepper on the nose unfold into warm almond, gentle spices, candied orange, and soft oak.',
          'A cask that deepens the spirit rather than transforming it.'
        ],
        age: '5 Years',
        volume: '750ml',
        availability: 'Limited quantities. Made in Vanuatu.',
        status: 'limited quantities'
      },
      {
        title: 'American Virgin Oak',
        uniqueBlend: 'Five years in char #4 virgin American oak with no previous fill and no borrowed influence.',
        story: [
          'Raw oak and Vanuatu time bring vanilla, caramel, stone fruit, and pepper on the nose.',
          'The palate is silky and deeply oaked, moving through praline, dried date, and cocoa.'
        ],
        age: '5 Years',
        volume: '750ml',
        availability: 'Limited quantities. Made in Vanuatu.',
        status: 'limited quantities'
      }
    ]
  }
}

const collection = computed(() => {
  const slug = String(route.params.slug || '').toLowerCase()
  return collectionsData[slug] || null
})

// Dynamic SEO meta tags
useHead({
  title: computed(() => collection.value ? `${collection.value.name} | 83 Islands Distillery` : 'Collection | 83 Islands Distillery'),
  meta: [
    {
      name: 'description',
      content: computed(() => collection.value ? collection.value.tagline : 'Explore our exclusive collection of exceptional spirits from Vanuatu.')
    },
    {
      property: 'og:title',
      content: computed(() => collection.value ? `${collection.value.name} | 83 Islands Distillery` : 'Collection | 83 Islands Distillery')
    },
    {
      property: 'og:description',
      content: computed(() => collection.value ? collection.value.tagline : 'Explore our exclusive collection of exceptional spirits from Vanuatu.')
    },
    {
      property: 'og:url',
      content: computed(() => `https://83islands.com/collections/${route.params.slug}`)
    }
  ]
})
</script>
